import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  doc
} from "firebase/firestore";

import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Pagination
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";

export default function StoreLedger() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 3000,
    showConfirmButton: false
  });

  const [propertyId, setPropertyId] = useState(null);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ---------------- FETCH PROPERTY ---------------- */

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const snap = await getDoc(doc(db, "authroles", user.uid));
        if (snap.exists()) {
          const propId = snap.data().propertyId;
          setPropertyId(propId);
          fetchDepartments(propId);
          fetchProducts(propId);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  /* ---------------- FETCH DEPARTMENTS ---------------- */

  const fetchDepartments = (propId) => {
    const q = query(
      collection(db, "departments"),
      where("propertyId", "==", propId)
    );

    onSnapshot(q, (snapshot) => {
      setDepartments(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
      );
    });
  };

  /* ---------------- FETCH PRODUCTS ---------------- */

  const fetchProducts = (propId) => {
    const q = query(
      collection(db, "storeproducts"),
      where("propertyId", "==", propId),
      orderBy("product", "asc")
    );

    onSnapshot(q, async (snapshot) => {
      const productData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      /* ---- Fetch avak & javak filtered by property ---- */

      const [avakSnapshot, javakSnapshot] = await Promise.all([
        getDocs(
          query(
            collection(db, "avakitems"),
            where("propertyId", "==", propId)
          )
        ),
        getDocs(
          query(
            collection(db, "javakitems"),
            where("propertyId", "==", propId)
          )
        )
      ]);

      const avakMap = {};
      const javakMap = {};

      avakSnapshot.forEach((doc) => {
        const { productid, quantity } = doc.data();
        avakMap[productid] =
          (avakMap[productid] || 0) + (parseInt(quantity) || 0);
      });

      javakSnapshot.forEach((doc) => {
        const { productid, quantity } = doc.data();
        javakMap[productid] =
          (javakMap[productid] || 0) + (parseInt(quantity) || 0);
      });

      const combined = productData.map((item) => ({
        ...item,
        avakqtytotal: avakMap[item.id] || 0,
        javakqtytotal: javakMap[item.id] || 0
      }));

      setProducts(combined);
      setCurrentPage(1);
    });
  };

  /* ---------------- SEARCH ---------------- */

  const filtered = products.filter((item) =>
    item.product?.toLowerCase().includes(searchText.toLowerCase())
  );

  /* ---------------- PAGINATION ---------------- */

  const indexLast = currentPage * itemsPerPage;
  const indexFirst = indexLast - itemsPerPage;
  const currentItems = filtered.slice(indexFirst, indexLast);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  /* ---------------- UI ---------------- */

  return (
    <Box className="content-wrapper" sx={{ p: 4, backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Store Ledger
      </Typography>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>

          <TextField
            label="Search Product"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr No</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Avak Qty</TableCell>
                <TableCell>Javak Qty</TableCell>
                <TableCell>Available</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {currentItems.map((item, index) => {
                const departmentName =
                  departments.find((d) => d.id === item.departmentid)
                    ?.department || "";

                const available =
                  (item.avakqtytotal || 0) -
                  (item.javakqtytotal || 0);

                return (
                  <TableRow key={item.id}>
                    <TableCell>{indexFirst + index + 1}</TableCell>
                    <TableCell>{departmentName}</TableCell>
                    <TableCell>{item.product}</TableCell>
                    <TableCell>{item.avakqtytotal}</TableCell>
                    <TableCell>{item.javakqtytotal}</TableCell>
                    <TableCell>{available}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        href={`view-store-ledger/${item.id}`}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
}